import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { canAccess } from '../utils/canAccess';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
  Card,
  Badge,
  Dropdown,
  InputGroup
} from 'react-bootstrap';

import Select from 'react-select';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';

import {
  FaUser,
  FaTools,
  FaEdit,
  FaTrash,
  FaPlus,
  FaCalendarAlt,
  FaShieldAlt,
  FaPaperPlane,
  FaSearch,
  FaBroom,
  FaFileExcel,
  FaFileCsv,
  FaCheckCircle,
  FaExclamationTriangle,
  FaLock,
  FaUnlock,
  FaPaperclip,
  FaHardHat,
  FaUniversity,
  FaFileInvoiceDollar,
  FaEllipsisV,
  FaReceipt,
  FaFileUpload
} from 'react-icons/fa';
import KpiCard from '../components/KpiCard';

import DocumentsModal from '../components/DocumentsModal';
import StcModal from '../components/StcModal';
import documentService from '../services/documentService';
import '../styles/OuvrierPage.css';

const OuvrierPage = ({ user }) => {
  const canWrite     = user?.role !== 'pointeur' && canAccess(user, 'ouvriers:write');
  const canDelete    = user?.role !== 'pointeur' && canAccess(user, 'ouvriers:delete');
  const canSeeSalary = canAccess(user, 'ouvriers:all');

  const [ouvriers, setOuvriers] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [docCounts, setDocCounts] = useState({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [selected, setSelected] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formMode, setFormMode] = useState('add');

  const [showDocsModal, setShowDocsModal] = useState(false);
  const [selectedDocItem, setSelectedDocItem] = useState(null);

  const [showStcModal, setShowStcModal] = useState(false);
  const [selectedStcOuvrier, setSelectedStcOuvrier] = useState(null);

  const [showFicheModal, setShowFicheModal] = useState(false);
  const [ficheOuvrier, setFicheOuvrier] = useState(null);
  const [ficheStats, setFicheStats] = useState(null);
  const openFiche = async (o) => {
    setFicheOuvrier(o);
    setFicheStats(null);
    setShowFicheModal(true);
    try {
      const res = await axios.get(`/ouvriers/${o.id}/pointage-stats`);
      setFicheStats(res.data);
    } catch { /* stats non critiques */ }
  };

  const emptyForm = {
    name: '',
    cin: '',
    salary: '',
    qualification: '',
    chantier_id: '',
    observation: '',
    cnss: '',
    rib: '',
    date_stc: '',
    bloque: false,
    matricule_sage: '',
    date_entree: '',
    societe: ''
  };

  const [formData, setFormData] = useState({ ...emptyForm });

  const [showAllNonBloque, setShowAllNonBloque] = useState(false);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);

  const [chantierFilterIds, setChantierFilterIds] = useState([]);
  const [cnssFilter, setCnssFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [bloqueFilter, setBloqueFilter] = useState('tous'); // 'tous' | 'actifs' | 'bloques'
  const [sortField, setSortField] = useState('id');
  const [sortDir,   setSortDir]   = useState('asc');
  const [page,      setPage]      = useState(1);
  const PAGE_SIZE = 25;

  const cycleBloqueFilter = () => {
    setBloqueFilter(v => v === 'tous' ? 'actifs' : v === 'actifs' ? 'bloques' : 'tous');
    setPage(1);
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  };

  const normalizeText = (s = '') =>
    String(s)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const fetchData = async (allNonBloque = false) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const url = allNonBloque ? '/ouvriers?nonbloque=1' : '/ouvriers';
      const [resOuvriers, resChantiers] = await Promise.all([
        axios.get(url),
        axios.get('/chantiers')
      ]);

      const ouvriersData = Array.isArray(resOuvriers.data) ? resOuvriers.data : [];
      setOuvriers(ouvriersData);
      setChantiers(Array.isArray(resChantiers.data) ? resChantiers.data : []);

      if (ouvriersData.length) {
        const statsRes = await documentService.getDocumentsStats(
          'ouvrier',
          ouvriersData.map((o) => o.id)
        );
        setDocCounts(statsRes?.data || {});
      } else {
        setDocCounts({});
      }
    } catch (err) {
      console.error(err);
      setError('❌ Erreur lors du chargement des données.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(showAllNonBloque);
  }, [showAllNonBloque]);

  const kpi = useMemo(() => {
    const total = ouvriers.length;
    const cnssOk = ouvriers.filter((o) => normalizeText(o.cnss) === 'ok').length;
    const cnssNon = ouvriers.filter((o) => {
      const v = normalizeText(o.cnss);
      return v === 'non declare' || v === 'non déclaré';
    }).length;
    const ribOk = ouvriers.filter((o) => (o.rib || '').trim().length >= 10).length;
    const avecStc = ouvriers.filter((o) => !!o.date_stc).length;
    const bloques = ouvriers.filter((o) => !!o.bloque).length;

    return { total, cnssOk, cnssNon, ribOk, avecStc, bloques };
  }, [ouvriers]);

  const chantierOptions = useMemo(() => {
    const base = (chantiers || []).map((c) => ({ value: c.id, label: c.name }));
    return [{ value: '__ALL__', label: '✅ Tous les chantiers' }, ...base];
  }, [chantiers]);

  const chantierSelectValue = useMemo(() => {
    return chantierOptions.filter(
      (o) => typeof o.value === 'number' && chantierFilterIds.includes(o.value)
    );
  }, [chantierOptions, chantierFilterIds]);

  const handleChantierSelect = (selectedOptions) => {
    const arr = selectedOptions || [];
    const hasAll = arr.some((x) => x.value === '__ALL__');
    if (hasAll) {
      setChantierFilterIds((chantiers || []).map((c) => c.id));
      return;
    }
    setChantierFilterIds(arr.map((x) => x.value));
  };

  const resetFilters = () => {
    setChantierFilterIds([]);
    setCnssFilter('');
    setSearchText('');
    setBloqueFilter('tous');
  };

  const filteredOuvriers = useMemo(() => {
    const txt = normalizeText(searchText);

    return (ouvriers || []).filter((o) => {
      if (chantierFilterIds.length) {
        const id = Number(o?.chantier_id || 0);
        if (!chantierFilterIds.includes(id)) return false;
      }

      if (cnssFilter) {
        const v = normalizeText(o?.cnss || '');
        if (v !== normalizeText(cnssFilter)) return false;
      }

      if (bloqueFilter === 'actifs' && !!o?.bloque) return false;
      if (bloqueFilter === 'bloques' && !o?.bloque) return false;

      if (txt) {
        const chantierName = o?.Chantier?.name || '';
        const hay = normalizeText(
          [
            o?.id,
            o?.name,
            o?.cin,
            o?.qualification,
            chantierName,
            o?.cnss,
            o?.rib,
            o?.societe,
            o?.matricule_sage,
            o?.observation,
            o?.bloque ? 'bloque' : 'actif',
            o?.documents_count ?? 0
          ]
            .filter(Boolean)
            .join(' | ')
        );

        if (!hay.includes(txt)) return false;
      }

      return true;
    });
  }, [ouvriers, chantierFilterIds, cnssFilter, searchText, bloqueFilter]);

  const ouvriersWithDocs = useMemo(() => {
    return (filteredOuvriers || []).map((o) => ({
      ...o,
      documents_count: Number(docCounts[o.id] || 0)
    }));
  }, [filteredOuvriers, docCounts]);

  const sortedOuvriers = useMemo(() => {
    const arr = [...ouvriersWithDocs];
    arr.sort((a, b) => {
      let va, vb;
      if (sortField === 'chantier') {
        va = a.Chantier?.name || '';
        vb = b.Chantier?.name || '';
      } else {
        va = a[sortField] ?? '';
        vb = b[sortField] ?? '';
      }
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
    return arr;
  }, [ouvriersWithDocs, sortField, sortDir]);

  const totalPages   = Math.max(1, Math.ceil(sortedOuvriers.length / PAGE_SIZE));
  const pagedOuvriers = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedOuvriers.slice(start, start + PAGE_SIZE);
  }, [sortedOuvriers, page, PAGE_SIZE]);

  const SortIcon = ({ field }) => (
    <span style={{ opacity: sortField === field ? 1 : 0.25, marginLeft: 3, fontSize: '0.7em' }}>
      {sortField === field ? (sortDir === 'asc' ? '▲' : '▼') : '▲'}
    </span>
  );

  const renderCnssBadge = (cnss) => {
    const v = normalizeText(cnss);
    if (v === 'ok') {
      return (
        <Badge bg="success" className="op-badge">
          <FaCheckCircle className="me-1" /> OK
        </Badge>
      );
    }
    if (v === 'non declare' || v === 'non déclaré') {
      return (
        <Badge bg="warning" text="dark" className="op-badge">
          <FaExclamationTriangle className="me-1" /> Non déclaré
        </Badge>
      );
    }
    return <Badge bg="secondary" className="op-badge">—</Badge>;
  };



  const exportExcel = () => {
    try {
      const rows = (ouvriersWithDocs || []).map((o) => ({
        ID: o.id,
        Nom: o.name || '',
        CIN: o.cin || '',
        Qualification: o.qualification || '',
        Chantier: o?.Chantier?.name || '',
        Societe: o.societe || '',
        Date_Entree: o.date_entree ? String(o.date_entree).split('T')[0] : '',
        Matricule_Sage: o.matricule_sage || '',
        Statut: o.bloque ? 'Bloqué' : 'Actif',
        Documents: o.documents_count ?? 0,
        CNSS: o.cnss || '',
        RIB: o.rib || '',
        Date_STC: o.date_stc ? String(o.date_stc).split('T')[0] : '',
        ...(canSeeSalary ? { Salaire: o.salary ?? '' } : {})
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Ouvriers');
      XLSX.writeFile(wb, `Ouvriers_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`);
    } catch (e) {
      console.error(e);
      setError("❌ Erreur lors de l'export Excel.");
    }
  };

  const exportCSV = () => {
    try {
      const headers = [
        'id',
        'name',
        'cin',
        'qualification',
        'chantier',
        'societe',
        'date_entree',
        'matricule_sage',
        'statut',
        'documents',
        'cnss',
        'rib',
        'date_stc',
        ...(canSeeSalary ? ['salary'] : [])
      ];

      const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

      const lines = [
        headers.join(';'),
        ...(ouvriersWithDocs || []).map((o) => {
          const chantier = o?.Chantier?.name || '';
          const date_stc = o.date_stc ? String(o.date_stc).split('T')[0] : '';
          const statut = o.bloque ? 'Bloqué' : 'Actif';

          const map = {
            id: o.id,
            name: o.name,
            cin: o.cin,
            qualification: o.qualification,
            chantier,
            societe: o.societe,
            date_entree: o.date_entree ? String(o.date_entree).split('T')[0] : '',
            matricule_sage: o.matricule_sage,
            statut,
            documents: o.documents_count ?? 0,
            cnss: o.cnss,
            rib: o.rib,
            date_stc,
            salary: o.salary
          };

          return headers.map((h) => escape(map[h])).join(';');
        })
      ];

      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Ouvriers_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setError("❌ Erreur lors de l'export CSV.");
    }
  };

  const normalizeHeader = (h) => normalizeText(h).toUpperCase().replace(/[^A-Z0-9]/g, '');

  const pickHeader = (headerMap, candidates) => {
    for (const c of candidates) {
      const found = headerMap[normalizeHeader(c)];
      if (found) return found;
    }
    return null;
  };

  const formatImportedDate = (v) => {
    if (!v) return '';
    if (v instanceof Date) return format(v, 'yyyy-MM-dd');
    return String(v).trim();
  };

  const triggerImport = () => {
    if (importInputRef.current) importInputRef.current.click();
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setError('');
    setSuccess('');

    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array', cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });

      if (!raw.length) {
        setError('❗ Fichier vide ou format non reconnu.');
        return;
      }

      const headerMap = {};
      Object.keys(raw[0]).forEach((h) => { headerMap[normalizeHeader(h)] = h; });

      const idKey = pickHeader(headerMap, ['ID OUVRIER', 'ID_OUVRIER', 'ID']);
      const matriculeKey = pickHeader(headerMap, ['MATRICULE SAGE', 'MATRICULE_SAGE', 'MATRICULE']);
      const dateKey = pickHeader(headerMap, ["DATE D'ENTREE", 'DATE ENTREE', 'DATE_ENTREE']);
      const societeKey = pickHeader(headerMap, ['SOCIETE']);

      if (!idKey) {
        setError("❗ Colonne 'ID Ouvrier' introuvable dans le fichier.");
        return;
      }

      const rows = raw
        .map((r) => ({
          id_ouvrier: Number(r[idKey]),
          matricule_sage: matriculeKey ? String(r[matriculeKey] || '').trim() : '',
          date_entree: dateKey ? formatImportedDate(r[dateKey]) : '',
          societe: societeKey ? String(r[societeKey] || '').trim() : ''
        }))
        .filter((r) => r.id_ouvrier);

      if (!rows.length) {
        setError('❗ Aucune ligne exploitable trouvée (ID Ouvrier manquant).');
        return;
      }

      setImporting(true);
      const res = await axios.post('/ouvriers/import', { rows });
      const { updated = 0, notFound = [], forbidden = [] } = res.data || {};

      let msg = `✅ ${updated} ouvrier(s) mis à jour depuis Sage.`;
      if (notFound.length) {
        msg += ` ⚠️ ${notFound.length} ID introuvable(s) : ${notFound.slice(0, 5).join(', ')}${notFound.length > 5 ? '…' : ''}.`;
      }
      if (forbidden.length) {
        msg += ` ⛔ ${forbidden.length} hors de votre périmètre chantier.`;
      }
      setSuccess(msg);
      fetchData(showAllNonBloque);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "❌ Erreur lors de l'import Sage.");
    } finally {
      setImporting(false);
    }
  };

  const openAddModal = () => {
    setFormMode('add');
    setSelected(null);
    setFormData({ ...emptyForm });
    setShowFormModal(true);
  };

  const openEditModal = (item) => {
    setFormMode('edit');
    setSelected(item);

    setFormData({
      name: item.name || '',
      cin: item.cin || '',
      salary: item.salary ?? '',
      qualification: item.qualification || '',
      chantier_id: item.chantier_id || '',
      observation: item.observation || '',
      cnss: item.cnss || '',
      rib: item.rib || '',
      date_stc: item.date_stc ? String(item.date_stc).split('T')[0] : '',
      bloque: !!item.bloque,
      matricule_sage: item.matricule_sage || '',
      date_entree: item.date_entree ? String(item.date_entree).split('T')[0] : '',
      societe: item.societe || ''
    });

    setShowFormModal(true);
  };

  const openDeleteModal = (item) => {
    setSelected(item);
    setShowDeleteModal(true);
  };

  const openDocsModal = (item) => {
    setSelectedDocItem(item);
    setShowDocsModal(true);
  };

  const closeDocsModal = () => {
    setShowDocsModal(false);
    setSelectedDocItem(null);
    fetchData();
  };

  const openStcModal = (item) => {
    setSelectedStcOuvrier(item);
    setShowStcModal(true);
  };

  const closeStcModal = () => {
    setShowStcModal(false);
    setSelectedStcOuvrier(null);
    fetchData();
  };

  const sendToChantierTout = async (id) => {
    try {
      const ouvrier = ouvriers.find((o) => o.id === id);
      if (!ouvrier) return;

      await axios.put(`/ouvriers/${id}/envoyer-tout`);

      setSuccess(`✅ ${ouvrier.name} envoyé vers le chantier TOUT.`);
      fetchData();
    } catch (err) {
      console.error(err);
      setError('❌ Erreur lors de l’envoi vers chantier TOUT.');
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    if (!String(formData.name || '').trim()) return "⚠️ Le nom est obligatoire.";
    if (!String(formData.cin || '').trim()) return "⚠️ Le CIN est obligatoire.";
    if (canSeeSalary) {
      const s = Number(formData.salary || 0);
      if (Number.isNaN(s) || s < 0) return "⚠️ Salaire invalide.";
    }
    return '';
  };

  const handleFormSubmit = async () => {
    setError('');
    setSuccess('');

    const v = validateForm();
    if (v) {
      setError(v);
      return;
    }

    const updatedData = {
      ...formData,
      salary: parseFloat(formData.salary || 0),
      chantier_id: formData.chantier_id ? Number(formData.chantier_id) : null,
      date_stc: formData.date_stc || null,
      date_entree: formData.date_entree || null,
      bloque: !!formData.bloque
    };

    try {
      if (formMode === 'add') await axios.post('/ouvriers', updatedData);
      else await axios.put(`/ouvriers/${selected.id}`, updatedData);

      setShowFormModal(false);
      setSuccess('✅ Ouvrier enregistré.');
      fetchData();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || '❌ Erreur enregistrement ouvrier.');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/ouvriers/${selected.id}`);
      setShowDeleteModal(false);
      setSuccess('✅ Ouvrier supprimé.');
      fetchData();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || '❌ Erreur suppression.');
    }
  };

  const TH = ({ field, children, style = {} }) => (
    <th
      onClick={() => field && toggleSort(field)}
      style={{
        padding: '9px 12px',
        fontSize: '0.72rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: '#6d4e36',
        background: '#faf7f4',
        borderBottom: '2px solid rgba(109,78,54,0.18)',
        whiteSpace: 'nowrap',
        cursor: field ? 'pointer' : 'default',
        userSelect: 'none',
        ...style
      }}
    >
      {children}{field && <SortIcon field={field} />}
    </th>
  );

  const selectPropsCommon = {
    isMulti: true,
    isClearable: true,
    isSearchable: true,
    closeMenuOnSelect: false,
    classNamePrefix: 'react-select',
    className: 'op-select',
    menuPortalTarget: document.body,
    styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) }
  };

  return (
    <div className="container mt-4 mb-5 op-page">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h2 className="m-0">
            <FaHardHat className="me-2" />
            Ouvriers
          </h2>
          <div className="op-page-subtitle">
            {loading ? '...' : (
              <>
                <span className="op-subtitle-count">{ouvriers.length}</span> ouvrier{ouvriers.length !== 1 ? 's' : ''}
                {kpi.bloques > 0 && (
                  <span className="op-subtitle-warn ms-2">· {kpi.bloques} bloqué{kpi.bloques !== 1 ? 's' : ''}</span>
                )}
              </>
            )}
          </div>
        </div>

        <div className="d-flex gap-2 flex-wrap">
          <Button
            variant="outline-success"
            onClick={exportExcel}
            disabled={loading || ouvriersWithDocs.length === 0}
          >
            <FaFileExcel className="me-2" /> Export Excel
          </Button>

          <Button
            variant="outline-secondary"
            onClick={exportCSV}
            disabled={loading || ouvriersWithDocs.length === 0}
          >
            <FaFileCsv className="me-2" /> Export CSV
          </Button>

          {canSeeSalary && (
            <Button
              variant={showAllNonBloque ? 'warning' : 'outline-warning'}
              onClick={() => setShowAllNonBloque(v => !v)}
            >
              <FaSearch className="me-2" />
              {showAllNonBloque ? '📍 Mes chantiers' : '🌍 Tous (non bloqués)'}
            </Button>
          )}

          {canWrite && (
            <>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                ref={importInputRef}
                onChange={handleImportFile}
                style={{ display: 'none' }}
              />
              <Button
                variant="outline-primary"
                onClick={triggerImport}
                disabled={importing}
              >
                {importing ? (
                  <Spinner animation="border" size="sm" className="me-2" />
                ) : (
                  <FaFileUpload className="me-2" />
                )}
                Importer Sage
              </Button>
            </>
          )}

          {canWrite && (
            <Button variant="success" onClick={openAddModal}>
              <FaPlus className="me-2" />
              Ajouter
            </Button>
          )}
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      {showAllNonBloque && (
        <Alert variant="warning" className="py-2 mb-2">
          🌍 Mode actif : affichage de <strong>tous les ouvriers non bloqués</strong> de tous les chantiers
        </Alert>
      )}

      <Row className="g-3 mb-3">
        <Col xs={6} md={2}>
          <KpiCard title="Total ouvriers" value={kpi.total} color="#6d4e36" icon={FaHardHat} />
        </Col>
        <Col xs={6} md={2}>
          <KpiCard title="CNSS OK" value={kpi.cnssOk} color="#2e7d32" icon={FaCheckCircle} />
        </Col>
        <Col xs={6} md={2}>
          <KpiCard title="CNSS Non déclaré" value={kpi.cnssNon} color="#e65100" icon={FaExclamationTriangle} alert={kpi.cnssNon > 0} />
        </Col>
        <Col xs={6} md={2}>
          <KpiCard title="RIB valide" value={kpi.ribOk} color="#1565c0" icon={FaUniversity} />
        </Col>
        <Col xs={6} md={2}>
          <KpiCard title="Avec STC" value={kpi.avecStc} color="#6f42c1" icon={FaReceipt} />
        </Col>
        <Col xs={6} md={2}>
          <KpiCard title="Bloqués" value={kpi.bloques} color="#b71c1c" icon={FaLock} alert={kpi.bloques > 0} />
        </Col>
      </Row>

      {/* Hero search bar */}
      <div className="op-search-hero mb-3">
        <InputGroup>
          <InputGroup.Text className="op-search-icon">
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            className="op-search-input"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Rechercher par nom, CIN, chantier, qualification..."
            autoComplete="off"
          />
          {searchText && (
            <Button variant="outline-secondary" onClick={() => setSearchText('')} title="Effacer">
              ×
            </Button>
          )}
        </InputGroup>
      </div>

      <Card className="op-filter-card mb-3">
        <Card.Body className="py-2">
          <Row className="g-2 align-items-end">
            <Col md={6}>
              <Form.Label className="small fw-bold mb-1">
                <FaTools className="me-1" /> Chantier
              </Form.Label>
              <Select
                {...selectPropsCommon}
                options={chantierOptions}
                value={chantierSelectValue}
                onChange={handleChantierSelect}
                placeholder="Tous les chantiers..."
              />
            </Col>

            <Col md={3}>
              <Form.Label className="small fw-bold mb-1">
                <FaShieldAlt className="me-1" /> CNSS
              </Form.Label>
              <Form.Select value={cnssFilter} onChange={(e) => setCnssFilter(e.target.value)} size="sm">
                <option value="">Tous</option>
                <option value="ok">✅ OK</option>
                <option value="non déclaré">⚠️ Non déclaré</option>
              </Form.Select>
            </Col>

            <Col md={3} className="d-flex align-items-end gap-2 justify-content-end flex-wrap">
              <Button
                size="sm"
                variant={
                  bloqueFilter === 'actifs'  ? 'success' :
                  bloqueFilter === 'bloques' ? 'danger'  : 'outline-secondary'
                }
                onClick={cycleBloqueFilter}
                className="op-bloque-toggle"
                title="Cliquer pour changer le filtre statut"
              >
                {bloqueFilter === 'actifs'  ? <><FaUnlock className="me-1" />Actifs</> :
                 bloqueFilter === 'bloques' ? <><FaLock   className="me-1" />Bloqués</> :
                                             <><FaUser   className="me-1" />Tous</>}
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={resetFilters}>
                <FaBroom className="me-1" /> Réinitialiser
              </Button>
              <span className="op-result-badge">
                {ouvriersWithDocs.length} résultat{ouvriersWithDocs.length !== 1 ? 's' : ''}
              </span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="d-flex align-items-center gap-2 mt-3">
          <Spinner animation="border" />
          <span>Chargement des ouvriers...</span>
        </div>
      ) : (
        <div className="op-table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr>
                <TH field="id"         style={{ width: 55, textAlign: 'center' }}>#</TH>
                <TH field="name"       style={{ minWidth: 160 }}>Nom</TH>
                <TH field="cin"        style={{ width: 120 }}>CIN</TH>
                <TH field="qualification" style={{ minWidth: 130 }}>Qualification</TH>
                <TH field="chantier"   style={{ minWidth: 130 }}>Chantier</TH>
                <TH field="societe"      style={{ minWidth: 110 }}>Société</TH>
                <TH field="date_entree"  style={{ width: 110, textAlign: 'center' }}>Date entrée</TH>
                <TH field="matricule_sage" style={{ width: 110 }}>Matricule Sage</TH>
                <TH field="cnss"       style={{ width: 110, textAlign: 'center' }}>CNSS</TH>
                <TH field="rib"        style={{ minWidth: 140 }}>RIB</TH>
                <TH field="date_stc"   style={{ width: 120, textAlign: 'center' }}>STC</TH>
                {canSeeSalary && <TH field="montant_stc" style={{ width: 130, textAlign: 'right' }}>Dernier STC</TH>}
                {canSeeSalary && <TH field="salary"            style={{ width: 90,  textAlign: 'right' }}>Taux/j</TH>}
                <TH style={{ width: 110, textAlign: 'center' }}>Actions</TH>
              </tr>
            </thead>
            <tbody>
              {pagedOuvriers.length === 0 ? (
                <tr>
                  <td colSpan="15" className="op-empty-state">
                    <FaHardHat className="op-empty-icon" />
                    <div>Aucun ouvrier trouvé</div>
                  </td>
                </tr>
              ) : pagedOuvriers.map((o, i) => (
                <tr
                  key={o.id}
                  className={o.bloque ? 'op-row-bloque' : ''}
                  style={{ borderBottom: '1px solid rgba(109,78,54,0.08)', background: i % 2 === 0 ? '#fff' : '#fdfaf7', cursor: 'pointer' }}
                  onDoubleClick={() => openFiche(o)}
                >
                  <td style={{ textAlign: 'center', color: '#bbb', fontSize: '0.78rem', padding: '9px 12px' }}>{o.id}</td>
                  <td style={{ padding: '9px 12px' }}>
                    <div className="op-name-cell">
                      <span className="op-name-text">{o.name}</span>
                      {o.bloque && <FaLock className="op-name-locked" title="Bloqué" />}
                    </div>
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.82rem', padding: '9px 12px' }}>{o.cin}</td>
                  <td style={{ padding: '9px 12px' }}>
                    <span className="op-qual-text" title={o.qualification}>{o.qualification || '—'}</span>
                  </td>
                  <td style={{ padding: '9px 12px' }}>
                    {o.Chantier?.name
                      ? <span className="op-chantier-chip">{o.Chantier.name}</span>
                      : <span className="text-muted">—</span>}
                  </td>
                  <td style={{ padding: '9px 12px' }}>{o.societe || <span className="text-muted">—</span>}</td>
                  <td style={{ textAlign: 'center', padding: '9px 12px' }}>
                    {o.date_entree ? String(o.date_entree).split('T')[0] : <span className="text-muted">—</span>}
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.82rem', padding: '9px 12px' }}>
                    {o.matricule_sage || <span className="text-muted">—</span>}
                  </td>
                  <td style={{ textAlign: 'center', padding: '9px 12px' }}>{renderCnssBadge(o.cnss)}</td>
                  <td style={{ padding: '9px 12px' }}>
                    {o.rib
                      ? <span className="op-rib-text" title={o.rib}>{o.rib}</span>
                      : <span className="text-muted">—</span>}
                  </td>
                  <td style={{ textAlign: 'center', padding: '9px 12px' }}>
                    {o.date_stc
                      ? <Badge bg="success" className="op-stc-badge"><FaCalendarAlt className="me-1" />{String(o.date_stc).split('T')[0]}</Badge>
                      : <Badge bg="light" text="secondary" className="op-stc-badge">—</Badge>}
                  </td>
                  {canSeeSalary && (
                    <td style={{ textAlign: 'right', padding: '9px 12px' }}>
                      {o.montant_stc && parseFloat(o.montant_stc) > 0
                        ? <span className="op-stc-amount">{Number(o.montant_stc).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} <small>DH</small></span>
                        : <span className="text-muted">—</span>}
                    </td>
                  )}
                  {canSeeSalary && (
                    <td style={{ textAlign: 'right', fontWeight: 600, padding: '9px 12px' }}>
                      <span className="op-salary-cell">{Number(o.salary ?? 0).toLocaleString('fr-FR')} <small>DH</small></span>
                    </td>
                  )}
                  <td style={{ textAlign: 'center', padding: '9px 12px' }}>
                    <div className="d-flex gap-1 justify-content-center align-items-center">
                      {canWrite && (
                        <Button size="sm" variant="warning" onClick={() => openEditModal(o)} className="op-action-btn" title="Modifier"><FaEdit /></Button>
                      )}
                      {canWrite && canDelete && (
                        <Button size="sm" variant="danger" onClick={() => openDeleteModal(o)} className="op-action-btn" title="Supprimer"><FaTrash /></Button>
                      )}
                      <Dropdown align="end">
                        <Dropdown.Toggle as={Button} size="sm" variant="outline-secondary" className="op-action-btn op-more-btn" id={`drop-${o.id}`} title="Plus d'actions">
                          <FaEllipsisV />
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="op-action-menu">
                          <Dropdown.Item onClick={() => openDocsModal(o)}>
                            <FaPaperclip className="me-2 text-primary" />Documents
                            {Number(o.documents_count || 0) > 0 && <Badge bg="primary" className="ms-2">{o.documents_count}</Badge>}
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => openStcModal(o)}>
                            <FaFileInvoiceDollar className="me-2 text-info" />STC
                            {o.date_stc && <Badge bg="success" className="ms-2">✓</Badge>}
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item onClick={() => sendToChantierTout(o.id)}>
                            <FaPaperPlane className="me-2 text-secondary" />Envoyer vers TOUT
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="op-pagination-bar">
              <button className="op-pag-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
              <span className="op-pag-info">
                Page <strong>{page}</strong> / {totalPages} · {sortedOuvriers.length} ouvrier{sortedOuvriers.length !== 1 ? 's' : ''}
              </span>
              <button className="op-pag-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          )}
        </div>
      )}

      <Modal show={showFormModal} onHide={() => setShowFormModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {formMode === 'add' ? '➕ Ajouter un ouvrier' : '✏️ Modifier un ouvrier'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>Nom *</Form.Label>
              <Form.Control name="name" value={formData.name} onChange={handleFormChange} />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>CIN *</Form.Label>
              <Form.Control name="cin" value={formData.cin} onChange={handleFormChange} />
            </Form.Group>

            {canSeeSalary && (
              <Form.Group className="mb-2">
                <Form.Label>Salaire (DH)</Form.Label>
                <Form.Control type="number" min="0" name="salary" value={formData.salary} onChange={handleFormChange} />
              </Form.Group>
            )}

            <Form.Group className="mb-2">
              <Form.Label>Qualification</Form.Label>
              <Form.Control name="qualification" value={formData.qualification} onChange={handleFormChange} />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Chantier</Form.Label>
              <Form.Select name="chantier_id" value={formData.chantier_id} onChange={handleFormChange}>
                <option value="">Sélectionner...</option>
                {chantiers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Société</Form.Label>
              <Form.Control name="societe" value={formData.societe} onChange={handleFormChange} />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Date d'entrée</Form.Label>
              <Form.Control type="date" name="date_entree" value={formData.date_entree} onChange={handleFormChange} />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Matricule Sage</Form.Label>
              <Form.Control name="matricule_sage" value={formData.matricule_sage} onChange={handleFormChange} />
            </Form.Group>

            {canWrite && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="bloque-switch"
                  name="bloque"
                  checked={!!formData.bloque}
                  onChange={handleFormChange}
                  label={formData.bloque ? '🔒 Ouvrier bloqué (ne sera pas pointé)' : '🔓 Ouvrier actif'}
                />
                <div className="small text-muted">
                  ℹ️ Si bloqué, l’ouvrier n’apparaîtra pas dans le pointage journalier.
                </div>
              </Form.Group>
            )}

            <Form.Group className="mb-2">
              <Form.Label>CNSS</Form.Label>
              <Form.Select name="cnss" value={formData.cnss} onChange={handleFormChange}>
                <option value="">-- Sélectionner --</option>
                <option value="ok">✅ OK</option>
                <option value="non déclaré">⚠️ Non déclaré</option>
              </Form.Select>
            </Form.Group>

           

            <Form.Group className="mb-2">
              <Form.Label>Date STC</Form.Label>
              <Form.Control type="date" name="date_stc" value={formData.date_stc} onChange={handleFormChange} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>RIB</Form.Label>
              <Form.Control name="rib" value={formData.rib} onChange={handleFormChange} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Observation</Form.Label>
              <Form.Control as="textarea" rows={3} name="observation" value={formData.observation} onChange={handleFormChange} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFormModal(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleFormSubmit}>
            Enregistrer
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Supprimer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Confirmer la suppression de <strong>{selected?.name}</strong> ?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={!canDelete}>
            Supprimer
          </Button>
        </Modal.Footer>
      </Modal>

      <DocumentsModal
        show={showDocsModal}
        onHide={closeDocsModal}
        module="ouvrier"
        moduleId={selectedDocItem?.id}
        itemLabel={
          selectedDocItem
            ? `${selectedDocItem.name || ''}${selectedDocItem.cin ? ` - ${selectedDocItem.cin}` : ''}`
            : ''
        }
        title="Documents de l'ouvrier"
        canDelete={canWrite}
      />

      <StcModal
        show={showStcModal}
        onHide={closeStcModal}
        ouvrier={selectedStcOuvrier}
        canWrite={canWrite}
      />

      {/* ── Fiche ouvrier (double-clic) ── */}
      <Modal show={showFicheModal} onHide={() => setShowFicheModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ background: '#fdf6ee', borderBottom: '2px solid rgba(109,78,54,0.15)' }}>
          <Modal.Title className="d-flex align-items-center gap-2">
            <FaHardHat style={{ color: '#8B5E3C' }} />
            <span>{ficheOuvrier?.name}</span>
            {ficheOuvrier?.bloque
              ? <Badge bg="danger" className="ms-2"><FaLock className="me-1" />Bloqué</Badge>
              : <Badge bg="success" className="ms-2"><FaCheckCircle className="me-1" />Actif</Badge>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#fffdf9' }}>
          {ficheOuvrier && (
            <>
              <Row className="g-3 mb-3">
                <Col md={3} sm={6}>
                  <div className="text-muted small mb-1">ID</div>
                  <div className="fw-semibold">#{ficheOuvrier.id}</div>
                </Col>
                <Col md={3} sm={6}>
                  <div className="text-muted small mb-1">CIN</div>
                  <div className="fw-semibold" style={{ fontFamily: 'monospace' }}>{ficheOuvrier.cin || '—'}</div>
                </Col>
                <Col md={6} sm={12}>
                  <div className="text-muted small mb-1">Qualification</div>
                  <div className="fw-semibold">{ficheOuvrier.qualification || '—'}</div>
                </Col>
                <Col md={6} sm={12}>
                  <div className="text-muted small mb-1"><FaHardHat className="me-1" />Chantier affecté</div>
                  <div className="fw-semibold">
                    {ficheOuvrier.Chantier?.name
                      ? <span className="op-chantier-chip">{ficheOuvrier.Chantier.name}</span>
                      : <span className="text-muted">—</span>}
                  </div>
                </Col>
                <Col md={3} sm={6}>
                  <div className="text-muted small mb-1">Société</div>
                  <div className="fw-semibold">{ficheOuvrier.societe || '—'}</div>
                </Col>
                <Col md={3} sm={6}>
                  <div className="text-muted small mb-1"><FaCalendarAlt className="me-1" />Date d'entrée</div>
                  <div className="fw-semibold">
                    {ficheOuvrier.date_entree ? String(ficheOuvrier.date_entree).split('T')[0] : '—'}
                  </div>
                </Col>
                <Col md={3} sm={6}>
                  <div className="text-muted small mb-1">Matricule Sage</div>
                  <div className="fw-semibold" style={{ fontFamily: 'monospace' }}>{ficheOuvrier.matricule_sage || '—'}</div>
                </Col>
                <Col md={3} sm={6}>
                  <div className="text-muted small mb-1"><FaShieldAlt className="me-1" />CNSS</div>
                  <div>{renderCnssBadge(ficheOuvrier.cnss)}</div>
                </Col>
                <Col md={3} sm={6}>
                  <div className="text-muted small mb-1">Documents</div>
                  <div>
                    <Badge bg={docCounts[ficheOuvrier.id] > 0 ? 'primary' : 'light'} text={docCounts[ficheOuvrier.id] > 0 ? undefined : 'secondary'}>
                      <FaPaperclip className="me-1" />{docCounts[ficheOuvrier.id] || 0}
                    </Badge>
                  </div>
                </Col>
                <Col md={6} sm={12}>
                  <div className="text-muted small mb-1"><FaUniversity className="me-1" />RIB</div>
                  <div className="fw-semibold" style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{ficheOuvrier.rib || '—'}</div>
                </Col>
                {canSeeSalary && (
                  <Col md={3} sm={6}>
                    <div className="text-muted small mb-1"><FaReceipt className="me-1" />Salaire de base</div>
                    <div className="fw-bold" style={{ color: '#2e7d32' }}>
                      {Number(ficheOuvrier.salary ?? 0).toLocaleString('fr-FR')} <small>DH</small>
                    </div>
                  </Col>
                )}
                <Col md={3} sm={6}>
                  <div className="text-muted small mb-1"><FaCalendarAlt className="me-1" />Date STC</div>
                  <div>
                    {ficheOuvrier.date_stc
                      ? <Badge bg="success"><FaCalendarAlt className="me-1" />{String(ficheOuvrier.date_stc).split('T')[0]}</Badge>
                      : <span className="text-muted">—</span>}
                  </div>
                </Col>
                {canSeeSalary && (
                  <Col md={3} sm={6}>
                    <div className="text-muted small mb-1"><FaFileInvoiceDollar className="me-1" />Montant STC</div>
                    <div className="fw-semibold">
                      {ficheOuvrier.montant_stc && parseFloat(ficheOuvrier.montant_stc) > 0
                        ? <>{Number(ficheOuvrier.montant_stc).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} <small>DH</small></>
                        : <span className="text-muted">—</span>}
                    </div>
                  </Col>
                )}
              </Row>
              <Row className="g-3 mt-0">
                <Col md={6} sm={12}>
                  <div style={{ background: '#f0f7ff', border: '1px solid #b3d4f5', borderRadius: 8, padding: '10px 14px' }}>
                    <div className="text-muted small mb-1"><FaCalendarAlt className="me-1" />Dernier pointage</div>
                    {ficheStats === null
                      ? <Spinner animation="border" size="sm" />
                      : <div className="fw-semibold">{ficheStats.dernier_pointage || <span className="text-muted">—</span>}</div>}
                  </div>
                </Col>
                <Col md={6} sm={12}>
                  <div style={{ background: '#f0fff4', border: '1px solid #a3d9b1', borderRadius: 8, padding: '10px 14px' }}>
                    <div className="text-muted small mb-1">
                      <FaCalendarAlt className="me-1" />
                      {ficheOuvrier.date_stc ? '1er pointage après STC' : '1er pointage'}
                    </div>
                    {ficheStats === null
                      ? <Spinner animation="border" size="sm" />
                      : <div className="fw-semibold">
                          {(ficheOuvrier.date_stc ? ficheStats.premier_pointage_post_stc : ficheStats.premier_pointage) || <span className="text-muted">—</span>}
                        </div>}
                  </div>
                </Col>
              </Row>
              {ficheOuvrier.observation && (
                <div className="mt-3" style={{ background: '#fdf0dc', border: '1px solid rgba(109,78,54,0.2)', borderRadius: 8, padding: '10px 14px' }}>
                  <div className="text-muted small mb-1">Observation</div>
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>{ficheOuvrier.observation}</div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: '#fdf6ee' }}>
          {canWrite && (
            <Button variant="warning" size="sm" onClick={() => { setShowFicheModal(false); openEditModal(ficheOuvrier); }}>
              <FaEdit className="me-1" />Modifier
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={() => setShowFicheModal(false)}>Fermer</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OuvrierPage;